import { useEffect, useState } from 'react';
import {
  getEfficacyMetrics,
  type EfficacyMetrics,
} from '../../services/efficacy';

// Hardening 2.4 (2026-08) — Métricas de eficácia da detecção (R7).
// Consolida em 1 tela o que antes exigia cruzar 4 páginas: FP real por
// event_type, funil kicks→bans (auto vs manual), sessões silenciosas/dia,
// distribuição de decisões, top event_types e detectores que sumiram.
// Sem lib de chart no projeto → barras CSS simples (padrão Sparkline.tsx).

const FP_ALERT_THRESHOLD = 0.05; // >5% FP bloqueia ship (política Erro 52)

export function Efficacy() {
  const [metrics, setMetrics] = useState<EfficacyMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(14);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEfficacyMetrics(days)
      .then((m) => {
        if (!cancelled) setMetrics(m);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'erro ao carregar métricas');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-200">Eficácia da detecção</h2>
          <p className="text-xs text-gray-500 mt-1">
            Hardening 2.4 — responde se "hack passando" é detector cego, detector
            desligado (kill switch) ou enforcement fraco. FP &gt; 5% bloqueia ship.
          </p>
        </div>
        <label className="text-sm text-gray-400 flex items-center gap-2">
          Janela:
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm"
          >
            <option value={7}>7d</option>
            <option value={14}>14d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
          </select>
        </label>
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading || !metrics ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <div className="space-y-6">
          <FunnelSection funnel={metrics.enforcement_funnel} />
          <FpRateSection rows={metrics.fp_rate_by_event_type} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SilentSection series={metrics.silent_sessions_per_day} />
            <DecisionSection dist={metrics.decision_distribution} />
          </div>
          <TopEventTypesSection rows={metrics.top_event_types} />
          <DetectorHealthSection rows={metrics.detector_health} days={metrics.days} />
          <p className="text-xs text-gray-600">
            Gerado em {new Date(metrics.generated_at).toLocaleString()} · janela de{' '}
            {metrics.days} dias
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Funil de enforcement: kicks → bans, auto (2.1) vs manual
// ============================================================

function FunnelSection({ funnel }: { funnel: EfficacyMetrics['enforcement_funnel'] }) {
  const autoTotal = funnel.ban_temp_auto + funnel.ban_perm_auto;
  const manualTotal = funnel.ban_temp_manual + funnel.ban_perm_manual;
  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-200 mb-3">
        Funil de enforcement
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Kicks" value={String(funnel.kicks)} color="orange"
              hint="decision_real = KICK no período" />
        <Card label="Ban temp" value={String(funnel.ban_temp)}
              hint={`auto ${funnel.ban_temp_auto} · manual ${funnel.ban_temp_manual}`} />
        <Card label="Ban perm" value={String(funnel.ban_perm)} color="red"
              hint={`auto ${funnel.ban_perm_auto} · manual ${funnel.ban_perm_manual}`} />
        <Card label="Auto (2.1)" value={String(autoTotal)}
              hint="via system:escalation" />
        <Card label="Manual" value={String(manualTotal)}
              hint="aplicado por operador" />
      </div>
    </section>
  );
}

// ============================================================
// FP rate por event_type (revisão humana de incidents)
// ============================================================

function FpRateSection({ rows }: { rows: EfficacyMetrics['fp_rate_by_event_type'] }) {
  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-200 mb-3">
        Taxa de FP por event_type{' '}
        <span className="text-xs font-normal text-gray-500">
          (revisão humana de incidents — alerta se &gt; 5%)
        </span>
      </h3>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhum incident revisado na janela.</p>
      ) : (
        <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Event type</th>
                <th className="text-right px-4 py-2">Revisados</th>
                <th className="text-right px-4 py-2">FP</th>
                <th className="text-right px-4 py-2">OK</th>
                <th className="text-right px-4 py-2">Taxa FP</th>
                <th className="text-right px-4 py-2">Backlog</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2f36]">
              {rows.map((r) => {
                const pct = r.fp_rate * 100;
                const alert = r.reviewed_total > 0 && r.fp_rate > FP_ALERT_THRESHOLD;
                return (
                  <tr key={r.event_type} className={alert ? 'bg-red-950/40' : ''}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-300">
                      {r.event_type}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-200">
                      {r.reviewed_total}
                    </td>
                    <td className="px-4 py-2 text-right text-red-400">{r.reviewed_fp}</td>
                    <td className="px-4 py-2 text-right text-emerald-400">
                      {r.reviewed_ok}
                    </td>
                    <td
                      className={`px-4 py-2 text-right font-semibold ${
                        alert ? 'text-red-400' : 'text-gray-200'
                      }`}
                    >
                      {r.reviewed_total === 0 ? '—' : `${pct.toFixed(1)}%`}
                      {alert && (
                        <span className="ml-1" title="FP acima de 5% — revisar detector">
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-amber-400">
                      {r.unreviewed}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================
// Sessões silenciosas por dia (SESSION_SILENT — Hardening 0.5)
// ============================================================

function SilentSection({ series }: { series: EfficacyMetrics['silent_sessions_per_day'] }) {
  const max = Math.max(1, ...series.map((d) => d.count));
  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-200 mb-3">
        Sessões silenciosas por dia
      </h3>
      <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4">
        <div className="flex items-end gap-[2px] h-28">
          {series.map((d) => (
            <div
              key={d.date}
              className="flex-1 bg-red-500/70 hover:bg-red-400 rounded-sm min-h-[2px]"
              style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
              title={`${d.date}: ${d.count}`}
            />
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-gray-600 mt-1">
          <span>{series[0]?.date}</span>
          <span>{series[series.length - 1]?.date}</span>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Distribuição de decisões efetivas
// ============================================================

const DECISION_COLORS: Record<string, string> = {
  ALLOW: 'bg-emerald-500/70',
  FLAG: 'bg-amber-500/70',
  KICK: 'bg-orange-500/70',
  BLOCK: 'bg-red-500/70',
};

function DecisionSection({ dist }: { dist: EfficacyMetrics['decision_distribution'] }) {
  const max = Math.max(1, ...dist.map((d) => d.count));
  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-200 mb-3">
        Decisões efetivas no período
      </h3>
      <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4 space-y-2">
        {dist.length === 0 ? (
          <p className="text-gray-500 text-sm">Nenhuma decisão na janela.</p>
        ) : (
          dist.map((d) => (
            <div key={d.decision} className="flex items-center gap-3 text-sm">
              <span className="w-14 font-mono text-xs text-gray-400">{d.decision}</span>
              <div className="flex-1 h-4 bg-[#0d0f12] rounded-sm overflow-hidden">
                <div
                  className={`h-full ${DECISION_COLORS[d.decision] ?? 'bg-gray-500/70'}`}
                  style={{ width: `${(d.count / max) * 100}%` }}
                />
              </div>
              <span className="w-10 text-right text-gray-200">{d.count}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ============================================================
// Top event_types por volume
// ============================================================

function TopEventTypesSection({ rows }: { rows: EfficacyMetrics['top_event_types'] }) {
  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-200 mb-3">
        Top 10 event_types por volume
      </h3>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhum evento na janela.</p>
      ) : (
        <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Event type</th>
                <th className="text-right px-4 py-2">Ocorrências</th>
                <th className="text-right px-4 py-2">Severidade média</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2f36]">
              {rows.map((t) => (
                <tr key={t.event_type}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-300">
                    {t.event_type}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-200">{t.occurrences}</td>
                  <td className="px-4 py-2 text-right text-gray-200">
                    {t.avg_severity.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================
// Detector health — event_types que sumiram (kill switch? detector morto?)
// ============================================================

function DetectorHealthSection({
  rows,
  days,
}: {
  rows: EfficacyMetrics['detector_health'];
  days: number;
}) {
  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-200 mb-3">
        Detectores sem ocorrência há ≥ 3 dias{' '}
        <span className="text-xs font-normal text-gray-500">
          (cruzar com Kill Switches pra distinguir desligado de cego)
        </span>
      </h3>
      {days < 7 ? (
        <p className="text-gray-500 text-sm">
          Janela &lt; 7 dias não separa "antes" de "últimos 3 dias" — amplie a janela.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Nenhum detector sumiu na janela — todos os tipos com volume recente.
        </p>
      ) : (
        <div className="bg-red-950/40 border border-red-900 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Event type</th>
                <th className="text-right px-4 py-2">Dias em silêncio</th>
                <th className="text-right px-4 py-2">Ocorrências anteriores</th>
                <th className="text-left px-4 py-2">Última ocorrência</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-red-900/50">
              {rows.map((d) => (
                <tr key={d.event_type}>
                  <td className="px-4 py-2 font-mono text-xs text-red-300">
                    {d.event_type}
                  </td>
                  <td className="px-4 py-2 text-right text-red-400 font-semibold">
                    {d.days_silent}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-200">
                    {d.occurrences_before}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-400">
                    {new Date(d.last_seen_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================
// Card (mesmo padrão visual do Reports)
// ============================================================

function Card({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: string;
  color?: 'amber' | 'orange' | 'red';
  hint?: string;
}) {
  const colors: Record<string, string> = {
    amber: 'text-amber-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
  };
  const valueClass = color ? colors[color] : 'text-gray-100';
  return (
    <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4">
      <p className="text-xs text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueClass}`}>{value}</p>
      {hint && <p className="text-[10px] text-gray-600 mt-1">{hint}</p>}
    </div>
  );
}
